'use client';

import { FC } from 'react';
import Image from 'next/image';
import FadeIn from '@/components/FadeIn/FadeIn';
import { ComparisonData, Benefit } from '../types';

interface WhatWeDoBetterProps {
  data: ComparisonData;
}

// Map benefit titles to mockup image paths based on competitor
const getMockupImageForBenefit = (
  title: string,
  competitorSlug: string
): string | null => {
  const normalizedTitle = title.toLowerCase();

  // Unify-specific mockups
  if (competitorSlug === 'unify') {
    if (normalizedTitle.includes('calling')) return '/images/compare/unify-calling.png';
    if (normalizedTitle.includes('crm')) return '/images/compare/unify-crm.png';
    if (normalizedTitle.includes('signal')) return '/images/compare/unify-signals.png';
    if (normalizedTitle.includes('task')) return '/images/compare/unify-tasks.png';
  }

  // Common Room-specific mockups
  if (competitorSlug === 'common-room') {
    if (normalizedTitle.includes('execution')) return '/images/compare/cr-execution.png';
    if (normalizedTitle.includes('calling')) return '/images/compare/cr-calling.png';
    if (normalizedTitle.includes('sdr')) return '/images/compare/cr-sdr.png';
  }

  return null;
};

interface BenefitCardProps {
  benefit: Benefit;
  competitorSlug: string;
}

const BenefitCard: FC<BenefitCardProps> = ({ benefit, competitorSlug }) => {
  const mockupImage = getMockupImageForBenefit(benefit.title, competitorSlug);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
      {/* Content at top */}
      <h3
        className="font-medium text-black mb-2"
        style={{ fontSize: 'clamp(1rem, 1.25vw, 1.25rem)' }}
      >
        {benefit.title}
      </h3>
      <p
        className="text-slate-600 mb-4"
        style={{ fontSize: 'clamp(0.8125rem, 0.9vw, 0.9375rem)', lineHeight: '1.5' }}
      >
        {benefit.description}
      </p>

      {/* Mockup image at bottom - use extracted Figma mockups */}
      <div className="flex-1 min-h-[200px] mt-2">
        {mockupImage ? (
          <div className="rounded-xl overflow-hidden bg-slate-50 h-full flex items-center justify-center p-4">
            <Image
              src={mockupImage}
              alt={benefit.title}
              width={600}
              height={400}
              className="w-full h-auto object-contain"
            />
          </div>
        ) : (
          <div className="rounded-xl bg-slate-100 h-full flex items-center justify-center">
            <span className="text-slate-400 text-sm">Mockup preview</span>
          </div>
        )}
      </div>
    </div>
  );
};

const WhatWeDoBetter: FC<WhatWeDoBetterProps> = ({ data }) => {
  const { benefits, competitor } = data;

  return (
    <section className="py-16 md:py-24">
      <div
        className="container-fluid px-4 md:px-7.5 mx-auto"
        style={{ maxWidth: 'clamp(1200px, 80vw, 1400px)' }}
      >
        <FadeIn variant="up" delay={0.1}>
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2
              className="font-medium text-black"
              style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.5rem)' }}
            >
              What{' '}
              <span style={{ color: '#094FFE' }}>marketbetter.ai</span>
              <br />
              does better?
            </h2>
          </div>

          {/* Benefits Grid - Responsive with fullWidth support */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <FadeIn
                key={index}
                variant="up"
                delay={0.1 + index * 0.05}
                className={benefit.fullWidth ? 'md:col-span-2' : ''}
              >
                <BenefitCard benefit={benefit} competitorSlug={competitor.slug} />
              </FadeIn>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default WhatWeDoBetter;
